/**
 * Highlights the parts of a section menu when the relevant section comes into view.
 *
 * This menu requires the menu items to have a link to a unique ID (<a href="#foo">) to work.
 */

class SectionMenu {
    private menuItems: NodeListOf<HTMLElement>;

    private menuTargets: Map<HTMLElement, Element>;

    private startOffset: number;

    private runningTimeout = 0;

    private lastScrollPosition: number;

    /**
     * @param element The menu element
     * @param menuItemSelector The selector to use to find the items within the menu; this must point to the whole menu
     *                         element and cannot be an <a> tag
     * @param activeClass The CSS class to assign to the active menu item
     * @param activeThreshold A number 0..1 where in the window (vertically) the item has to be to be marked as active
     *                        (0 = has to scroll all the way to the top, 0.5 = middle of the window, 1 = bottom of the window)
     * @param additionalOffset An addition vertical offset (in pixels) for the menu item to be marked active
     *                         This is required to offset the height of the menu.
     */
    public constructor(
        private readonly element: HTMLElement,
        private readonly menuItemSelector: string,
        private readonly activeClass: string,
        private readonly activeThreshold: number = 0.5,
        private readonly additionalOffset: number = 0
    ) {
        this.menuItems = this.element.querySelectorAll(this.menuItemSelector);
        this.menuTargets = new Map();
        this.findMenuTargets();

        // ensure the link looses the input focus after clicking, otherwise two elements would be highlighted when
        // scrolling further
        this.element.querySelectorAll('a').forEach(
            (el: HTMLAnchorElement) => el.addEventListener('click', (ev: MouseEvent) => {
                if (ev.target instanceof HTMLElement) {
                    ev.target.blur();
                }
            })
        );

        this.startOffset = element.scrollTop;
        this.lastScrollPosition = window.scrollY;

        window.addEventListener('scroll', () => this.handleScrollEvent());
        // set the initial state
        this.update();
    }

    public update(): void
    {
        this.resetActiveState();

        if (window.scrollY < this.startOffset) {
            return;
        }

        // we consider an element "active" if its headline is behind or above the menu, or in a given range (controlled
        // by "threshold") below the menu

        // the "top" part of the getBoundingClientRect() is compared against a threshold here:
        // if "top" is lower than the threshold, we consider the element active.
        // since getBoundingClientRect() is relative to the current browser window (upper left corner), we must do
        // relative calculations as well

        // this will make an element active when it's bounding box top is in the upper half of the window
        const topOffset: number = this.element.getBoundingClientRect().top + this.additionalOffset;
        const threshold: number = ((window.innerHeight - topOffset) * this.activeThreshold) + topOffset;

        const elementsAboveCurrentViewPort: Array<[HTMLElement, Element]> = [... this.menuTargets.entries()]
            .filter(([, target]: [HTMLElement, Element]) => target.getBoundingClientRect().top <= threshold)
            .reverse();
        const activeMenuItem: HTMLElement | null = elementsAboveCurrentViewPort[0][0] ?? null;
        if (activeMenuItem !== null) {
            activeMenuItem.classList.add(this.activeClass);
        }
    }

    private resetActiveState(): void {
        this.menuItems.forEach((element: HTMLElement) => element.classList.remove(this.activeClass));
    }

    private findMenuTargets(): void
    {
        this.menuItems.forEach((item: HTMLElement) => {
            const link: HTMLAnchorElement | null = item.querySelector('a');
            if (link === null) {
                console.error('Could not find link within menu element', item);
                return;
            }
            // the link href should just be #thetarget, so we should be able to find the element with it.
            const href: string | null = link.getAttribute('href');
            if (href === null) {
                return;
            }
            const targetElement: HTMLElement | null = document.querySelector(href) ?? document.querySelector(`a[name="${href.substring(1)}"]`);

            if (targetElement === null) {
                console.error('Could not find the target', href);
                return;
            }

            this.menuTargets.set(item, targetElement);
        });
    }

    private handleScrollEvent(): void
    {
        if (this.runningTimeout > 0) {
            clearTimeout(this.runningTimeout);
        }

        this.runningTimeout = window.setTimeout(() => {
            this.update();

        }, 100);
    }
}

export default SectionMenu;
